import DOMPurify from 'dompurify';

class TechSpecTable {
    /**
     * @param {HTMLElement} root
     */
    constructor (root) {
        /** @type {HTMLElement} */
        this.root = root;
        this.tableElm = root.querySelector('table');
        this.id = root.getAttribute('id');

        this.init();
    }

    /**
     * Initialises the class component
     */
    init(){
        if(!this.root || !this.tableElm) return;

        const ALLOWED_TAGS__EXCERPT_RTE = [
            'div',
            'table',
            'tbody',
            'thead',
            'tfoot',
            'tr',
            'th',
            'td',
            'a',
            'b',
            'br',
            'p',
            'span',
            'ol',
            'ul',
            'li',
            'em',
            'mark',
            'i',
        ];

        const ALLOWED_ATTR__EXCERPT_RTE = [
            'id',
            'class',
            'role',
        ];

        const tableHTML = DOMPurify.sanitize(this.tableElm, { ALLOWED_TAGS: ALLOWED_TAGS__EXCERPT_RTE, ALLOWED_ATTR: ALLOWED_ATTR__EXCERPT_RTE });
        this.tableHTML = new DOMParser().parseFromString(tableHTML, "text/html");

        this.tableElm.replaceWith(this.tableHTML.querySelector('table'));

        this.tableElm = this.root.querySelector('table');

        this.root.removeAttribute('id');
        this.tableElm.id = this.id;

        this.tableGroups = Array.from(this.tableElm.querySelectorAll('thead, tbody, tfoot'));
        this.tableRows = Array.from(this.tableElm.querySelectorAll('tr'));
        this.checkRowHeaders();

        this.tableHeaderCells = Array.from(this.tableElm.querySelectorAll('th'));
        this.tableDataCells = Array.from(this.tableElm.querySelectorAll('td'));
        this.formatCellContents();

        this.styleElm = document.createElement("style");
        document.head.appendChild(this.styleElm);
        this.stylesheet = this.styleElm.sheet;

        this.insertCellHeaders();
        this.insertTableAria();

        this.root.classList.add('has-initialised');
    }

    checkRowHeaders(){
        if(!this.tableRows) return;

        for (let i = 0; i < this.tableRows.length; i++) {
            let row = this.tableRows[i];
            if(row.firstElementChild.nodeName !== 'TH'){
                let rowFirstCellContent = row.firstElementChild.innerHTML;

                let rowColumnHeaderElm = document.createElement('th');
                rowColumnHeaderElm.innerHTML = rowFirstCellContent;

                row.firstElementChild.remove();
                row.prepend(rowColumnHeaderElm);
            }
        }
    }

    formatCellContents(){
        if(!this.tableDataCells) return;

        for (let i = 0; i < this.tableDataCells.length; i++) {
            let cell = this.tableDataCells[i];
            cell.innerHTML = '<p>' + DOMPurify.sanitize(cell.innerHTML, {ALLOWED_TAGS: ['br', 'strong'], ALLOWED_ATTR: []}) + '</p>';
        }
    }

    /**
     * Refreshes the class component and calls init() and does any necessary resets
     */
    refresh(){
        this.init();
    }

    /**
     * Inserts Header td values as psuedo ::before content into stylesheet element
     */
    insertCellHeaders(){
        if(!this.tableHeaderCells || !this.stylesheet) return;

        for (let i = 0; i < this.tableHeaderCells.length; i += 1) {
            this.stylesheet.insertRule(
                "#" + this.id + " td:nth-child(" + (i + 1) +  ")::before {" +
                    "content:'" + this.tableHeaderCells[i].innerText + "';" + 
                "}",
                this.stylesheet.cssRules.length
            );
        }
    }

    /**
     * Inserts required aria into table for it to still be accessible if display style is change in order to make table repsonsive
     */
    insertTableAria(){
        if(!this.tableGroups || !this.tableRows || !this.tableDataCells || !this.tableHeaderCells) return;

        this.tableElm.setAttribute('role','table');

        for (let i = 0; i < this.tableGroups.length; i++) {
            this.tableGroups[i].setAttribute('role','rowgroup');
        }

        for (let i = 0; i < this.tableRows.length; i++) {
            this.tableRows[i].setAttribute('role','row');
        }

        for (let i = 0; i < this.tableHeaderCells.length; i++) {
            this.tableHeaderCells[i].setAttribute('role','columnheader');
        }

        for (let i = 0; i < this.tableDataCells.length; i++) {
            this.tableDataCells[i].setAttribute('role','cell');
        }
    }
}

export { TechSpecTable }